/**
 * Week-view drag clone geometry.
 *
 * react-beautiful-dnd measures the dragged item once at lift and uses that
 * frozen box for both the clone's inline size and for collision detection.
 * Dragging an item out of the horizontally expanded day (~800px) over the
 * collapsed columns (~208px) therefore produces a clone four times too wide
 * whose drop target is decided by a point far from the pointer. These
 * helpers let us shrink the source node to column width before rbd measures
 * it (onBeforeCapture) and then resize the clone to whichever column it is
 * over, keeping the pointer at the same fraction inside it throughout.
 */

export const WEEK_DAY_DROPPABLE_PREFIX = 'week-day-';

/** The react-virtualized scroll container inside each day's DroppableDiv.
 * Rows are 100% of its content box and it excludes any scrollbar, so its
 * clientWidth is exactly the width an item row gets in that column. */
const ROW_CONTAINER_SELECTOR = '.ReactVirtualized__Grid';

/** Fast enough to feel attached to the pointer, slow enough not to flicker
 * when the clone skims a column boundary. */
const RESIZE_TRANSITION_MS = 120;

/** Extra width (px) the source must exceed the compact width by before we
 * bother compacting; absorbs flex rounding between collapsed columns. */
const COMPACT_TOLERANCE_PX = 1;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * @param {string|null|undefined} droppableId
 * @returns {boolean} true for a week-view day column droppable
 */
export const isWeekDayDroppableId = (droppableId) => (
  typeof droppableId === 'string' && droppableId.startsWith(WEEK_DAY_DROPPABLE_PREFIX)
);

/**
 * @param {string} droppableId a `week-day-*` droppable id
 * @returns {string} the day key (date string or program day number) after the prefix
 */
export const getWeekDayKeyFromDroppableId = (droppableId) => (
  droppableId.slice(WEEK_DAY_DROPPABLE_PREFIX.length)
);

/**
 * @param {string} draggableId
 * @param {Document} [doc]
 * @returns {HTMLElement|null} the in-list node rbd will measure for this draggable
 */
export const getDraggableNode = (draggableId, doc = document) => {
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(draggableId) : draggableId;
  return doc.querySelector(`[data-rbd-draggable-id="${escaped}"]`);
};

/**
 * @param {string} droppableId
 * @param {Document} [doc]
 * @returns {{ width: number, left: number }|null} the row width and client left
 *   edge of a column's item rows, or null when the column isn't in the DOM
 */
export const getWeekDayRowMetrics = (droppableId, doc = document) => {
  const node = doc.getElementById(droppableId)?.querySelector(ROW_CONTAINER_SELECTOR);
  if (!node) return null;
  return { width: node.clientWidth, left: node.getBoundingClientRect().left };
};

/**
 * @param {string} excludeDroppableId the drag's home column, ignored
 * @param {Document} [doc]
 * @returns {number|null} the narrowest row width among the other week-day
 *   columns, or null when there is no other column (single-day week)
 */
export const findCompactWeekDayWidth = (excludeDroppableId, doc = document) => {
  const lists = doc.querySelectorAll(`[data-exercise-list][id^="${WEEK_DAY_DROPPABLE_PREFIX}"]`);
  let min = null;
  lists.forEach((list) => {
    if (list.id === excludeDroppableId) return;
    const width = list.querySelector(ROW_CONTAINER_SELECTOR)?.clientWidth;
    if (width > 0 && (min === null || width < min)) min = width;
  });
  return min;
};

/**
 * Works out how to shrink the source item before rbd measures it so the
 * pointer keeps the same relative position inside the narrower box.
 *
 * @param {{ rect: { left: number, width: number }, pointerX: number,
 *   compactWidth: number|null }} args
 * @returns {{ grabFraction: number, shiftX: number, shouldCompact: boolean,
 *   capturedWidth: number }}
 *   grabFraction: 0..1 position of the pointer across the item;
 *   shiftX: translateX to apply with the compact width;
 *   capturedWidth: the width rbd will measure after any compaction
 */
export const computeCaptureGeometry = ({ rect, pointerX, compactWidth }) => {
  const grabOffset = clamp(pointerX - rect.left, 0, rect.width);
  const grabFraction = rect.width > 0 ? grabOffset / rect.width : 0;
  const shouldCompact = !!compactWidth && rect.width > compactWidth + COMPACT_TOLERANCE_PX;
  const shiftX = shouldCompact ? grabOffset * (1 - compactWidth / rect.width) : 0;
  return {
    grabFraction,
    shiftX,
    shouldCompact,
    capturedWidth: shouldCompact ? compactWidth : rect.width,
  };
};

/** rbd completes a drop only on a transitionend for `transform`, so its own
 * transition must stay intact; ours is appended, never substituted. */
const appendTransition = (base, extra) => (
  base && base !== 'none' ? `${base}, ${extra}` : extra
);

/**
 * Style overrides for the clone while it is being dragged.
 *
 * @param {{ rbdStyle: object, grabFraction: number, targetWidth: number }} args
 *   rbdStyle is provided.draggableProps.style; targetWidth the hovered column's row width
 * @returns {object} width/left/transition overrides to spread over rbdStyle
 */
export const getDraggingCloneStyle = ({ rbdStyle, grabFraction, targetWidth }) => ({
  width: targetWidth,
  left: rbdStyle.left + grabFraction * (rbdStyle.width - targetWidth),
  transition: appendTransition(
    rbdStyle.transition,
    `width ${RESIZE_TRANSITION_MS}ms ease, left ${RESIZE_TRANSITION_MS}ms ease`,
  ),
});

/**
 * Style overrides for the clone during rbd's drop animation. rbd moves its
 * frozen (compact) box to the destination, and for a cancelled drag or a
 * last-item return it moves back to the shifted lift position; either way
 * we land the visible clone flush with the landing column's rows ourselves:
 * final left = left + moveTo.x = landingLeft.
 *
 * @param {{ rbdStyle: object, landingLeft: number, landingWidth: number,
 *   dropAnimation: { duration: number, curve: string, moveTo: { x: number } } }} args
 * @returns {object} width/left/transition overrides to spread over rbdStyle
 */
export const getDropAnimatingCloneStyle = ({
  rbdStyle, landingLeft, landingWidth, dropAnimation,
}) => {
  const { duration, curve, moveTo } = dropAnimation;
  return {
    width: landingWidth,
    left: landingLeft - moveTo.x,
    transition: appendTransition(
      rbdStyle.transition,
      `width ${duration}s ${curve}, left ${duration}s ${curve}`,
    ),
  };
};
